import { Injectable, inject } from '@angular/core';
import { DevicesApiService } from '@birco/rainmesh-api';
import {DeviceStateService} from '../api/device-state-service.interface';
import {Observable} from 'rxjs';
import {DeviceFilter} from '../../../features/devices/state/device.state';
import {
  DeviceListResponseDto,
  DeviceResponseDto,
  DeviceUpdateRequest
} from '../../../features/devices/models/device-response.interface';

@Injectable({
  providedIn: 'root'
})
export class DeviceStateServiceImpl implements DeviceStateService {
  private devicesApiService = inject(DevicesApiService);


  getDevices(options: DeviceFilter): Observable<DeviceListResponseDto> {
    return this.devicesApiService.getDevices(
      options.pageSize,
      options.page,
      options.sortBy,
      options.sortDir,
      options.searchTerm,
      options.deviceTypes,
      options.isActive,
      options.isOutOfService,
      options.isFavorite,
    );
  }

  triggerDeviceAction(options: { id: string, value: boolean }): Observable<DeviceResponseDto> {
    return this.devicesApiService.triggerDeviceAction(options.id, {
      value: options.value,
    });
  }

  getDeviceById(options: { id: string }): Observable<DeviceResponseDto> {
    return this.devicesApiService.getDevice(options.id);
  }

  updateDevice(options: DeviceUpdateRequest): Observable<DeviceResponseDto> {
    const hasBatteryMinVoltage = Object.prototype.hasOwnProperty.call(options, 'batteryMinVoltage');
    const hasBatteryMaxVoltage = Object.prototype.hasOwnProperty.call(options, 'batteryMaxVoltage');
    const normalizedLocation = options.location
      ? {
        lat: options.location.lat,
        lon: options.location.lon,
      }
      : undefined;
    const normalizedBatteryMinVoltage = this.normalizeBatteryVoltageValue(options.batteryMinVoltage as number | null | undefined | string);
    const normalizedBatteryMaxVoltage = this.normalizeBatteryVoltageValue(options.batteryMaxVoltage as number | null | undefined | string);

    const payload = {
      ...(options.name !== undefined ? {displayName: options.name} : {}),
      ...(options.type !== undefined ? {type: options.type} : {}),
      ...(options.powerSource !== undefined ? {powerSource: options.powerSource ?? undefined} : {}),
      ...(options.isFavorite !== undefined ? {isFavorite: options.isFavorite} : {}),
      ...(options.isOutOfService !== undefined ? {isOutOfService: options.isOutOfService} : {}),
      ...(hasBatteryMinVoltage ? {batteryMinVoltage: normalizedBatteryMinVoltage} : {}),
      ...(hasBatteryMaxVoltage ? {batteryMaxVoltage: normalizedBatteryMaxVoltage} : {}),
      ...(options.location !== undefined ? {location: normalizedLocation} : {}),
      ...(options.sourcePumpId !== undefined ? {sourcePumpId: options.sourcePumpId ?? undefined} : {}),
      ...(options.sourcePumpThroughput !== undefined ? {sourcePumpThroughput: options.sourcePumpThroughput ?? undefined} : {}),
      ...(options.deviceTargetType !== undefined ? {deviceTargetType: options.deviceTargetType ?? undefined} : {}),
      ...(options.targetCisternId !== undefined ? {targetCisternId: options.targetCisternId ?? undefined} : {}),
    };

    return this.devicesApiService.updateDevice(options.id, payload as any);
  }

  getConnectedValves(options: { pumpId: string }): Observable<DeviceListResponseDto> {
    return this.devicesApiService.getConnectedValves(options.pumpId);
  }

  replaceConnectedValves(options: { pumpId: string, connectedValveIds: string[] }): Observable<DeviceListResponseDto> {
    return this.devicesApiService.replaceConnectedValves(options.pumpId, {
      connectedValveIds: options.connectedValveIds,
    });
  }

  private normalizeBatteryVoltageValue(value: unknown): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      const parsedValue = Number(value);
      return Number.isNaN(parsedValue) ? null : parsedValue;
    }

    return null;
  }
}
